import { deleteField, FieldPath, Timestamp, type DocumentReference } from 'firebase/firestore';
import type { CollectionEntry } from '../types';
import {
  COLLECTION_BAG_VERSION,
  type CollectionBagRow,
} from './collectionBag';
import { clampLotsToQuantities, serializeLots } from './collectionLots';
import {
  summarizeCollection,
  type CollectionSummary,
} from './collectionSummary';

/**
 * Whether a non-existent bag snapshot is safe to treat as a real empty bag
 * for full-document rewrites.
 *
 * Cached misses are NOT safe when the summary is unknown or says the user
 * still owns cards — persistence can store a prior miss before migrate.
 */
export function canTrustEmptyBagSnapshot(opts: {
  fromCache: boolean;
  /** `null` when collectionSummary has not loaded yet. */
  summaryUniqueCards: number | null;
}): boolean {
  if (!opts.fromCache) return true;
  return opts.summaryUniqueCards === 0;
}

/**
 * Base summary for a local bag mutation. Never invent empty totals over a
 * non-empty in-memory bag (quota can block ensureSummary).
 */
export function resolveMutationBaseSummary(
  hint: CollectionSummary | null | undefined,
  entries: Record<string, CollectionEntry>,
): CollectionSummary {
  if (hint) return hint;
  return summarizeCollection(entries, 1);
}

/**
 * Varargs for `WriteBatch.update` / `Transaction.update` / `updateDoc`.
 *
 * Card ids contain `/` (e.g. `ogn/001-a`). Dotted string paths like
 * `entries.${cardId}` are rejected by the Firestore client — use FieldPath
 * segments so the slash stays inside the map key.
 */
export function collectionBagEntryFieldUpdate(
  cardId: string,
  entry: CollectionEntry | undefined,
  revision: number,
): Array<string | FieldPath | unknown> {
  return [
    ...collectionBagHeaderFields(revision),
    ...collectionBagEntryFieldPairs(cardId, entry),
  ];
}

/** Bag-level fields every update must carry, independent of which cards moved. */
export function collectionBagHeaderFields(
  revision: number,
): Array<string | FieldPath | unknown> {
  return ['v', COLLECTION_BAG_VERSION, 'revision', revision, 'updatedAt', Timestamp.now()];
}

/**
 * The `entries` / `notes` pairs for one card. Split from the header so a flush
 * can carry many cards in a single update — one billed write regardless of how
 * many fields it sets.
 */
export function collectionBagEntryFieldPairs(
  cardId: string,
  entry: CollectionEntry | undefined,
): Array<string | FieldPath | unknown> {
  if (!entry || entry.quantity <= 0) {
    return [
      new FieldPath('entries', cardId),
      deleteField(),
      new FieldPath('notes', cardId),
      deleteField(),
    ];
  }
  const quantity = entry.quantity;
  const quantityFoil = Math.min(Math.max(0, entry.quantityFoil ?? 0), quantity);
  const lots = clampLotsToQuantities(entry.lots, quantity, quantityFoil);
  const row: CollectionBagRow = {
    q: quantity,
    f: quantityFoil,
    u: entry.updatedAt || Date.now(),
  };
  const serializedLots = serializeLots(lots);
  if (serializedLots) row.d = serializedLots;
  return [
    new FieldPath('entries', cardId),
    row,
    new FieldPath('notes', cardId),
    entry.notes?.trim() ? entry.notes : deleteField(),
  ];
}

type FieldUpdateTarget = {
  update: (
    ref: DocumentReference,
    field: string | FieldPath,
    value: unknown,
    ...more: unknown[]
  ) => unknown;
};

/** Apply a single-card bag patch via FieldPath-safe update varargs. */
export function applyCollectionBagEntryUpdate(
  target: FieldUpdateTarget,
  bagRef: DocumentReference,
  cardId: string,
  entry: CollectionEntry | undefined,
  revision: number,
): void {
  applyCollectionBagFieldUpdate(
    target,
    bagRef,
    collectionBagEntryFieldUpdate(cardId, entry, revision),
  );
}

/** Apply a prepared vararg list, which may cover any number of cards. */
export function applyCollectionBagFieldUpdate(
  target: FieldUpdateTarget,
  bagRef: DocumentReference,
  fields: Array<string | FieldPath | unknown>,
): void {
  const [first, second, ...rest] = fields;
  target.update(bagRef, first as string | FieldPath, second, ...rest);
}
